import { MAX_PLAYERS } from '../core/players';
import { NEON_ARENAS, PIRATE_ARENAS } from '../levels/catalog';
import { levelCapacity } from '../sim/level/schema';
import type { UserSettings } from './settingsStore';
import { presetIllustrationSvg } from './presetIllustration';
import './partyPresets.css';

export type PartyPresetId = 'classic' | 'arms-race' | 'office-brawl' | 'teams' | 'chaos' | 'pirate' | 'neon';
export type PartyPresetConfig = Pick<UserSettings,
  'mode' | 'mutators' | 'teams' | 'modeScoreLimit' | 'teamStocks' | 'rouletteSeconds' | 'enabledWeapons' | 'enabledLevels'
>;
export type PartyPreset = {
  id: PartyPresetId;
  title: string;
  description: string;
  accent: string;
  levelId: string;
};

/** These are combinations of existing rules, so every seat plays the same game. */
export const PARTY_PRESETS: readonly PartyPreset[] = [
  { id: 'classic', title: 'Classic Brawl', description: 'Last fighter standing wins.', accent: '#afd48b', levelId: 'woods-01' },
  { id: 'arms-race', title: 'Arms Race', description: 'Climb the weapon ladder.', accent: '#f1bb66', levelId: 'castle-01' },
  { id: 'office-brawl', title: 'Office Brawl', description: 'Chairs, staplers, overtime.', accent: '#92beca', levelId: 'office-overtime' },
  { id: 'teams', title: 'Team Brawl', description: 'Sun versus Moon. Team up.', accent: '#bdabeb', levelId: 'castle-crown-courtyard' },
  { id: 'chaos', title: 'Chaos Night', description: 'Wild weapons. Bigger launches.', accent: '#ed9288', levelId: 'factory-01' },
  { id: 'pirate', title: 'Pirate Brawl', description: 'Decks, docks, cannon fire.', accent: '#ddb278', levelId: 'pirate-black-sail' },
  { id: 'neon', title: 'Neon Brawl', description: 'Bright lights. Rooftop fights.', accent: '#7adcd3', levelId: 'neon-circuit' },
];

const themedLevels = { pirate: PIRATE_ARENAS, neon: NEON_ARENAS };
const sameSelection = (actual: readonly string[] | 'all' | undefined, expected: readonly string[] | 'all') => {
  if (expected === 'all') return actual === 'all' || actual === undefined;
  return Array.isArray(actual) && new Set(actual).size === expected.length
    && expected.every((value) => actual.includes(value));
};

/** Keep roster, accessibility, sound, and match-length preferences when choosing a different brawl. */
export function applyPartyPreset<T extends PartyPresetConfig>(settings: T, id: PartyPresetId): T {
  return {
    ...settings,
    mode: id === 'pirate' || id === 'neon' ? 'classic' : id === 'chaos' ? 'weapon-roulette' : id,
    mutators: id === 'chaos' ? ['low-gravity', 'giant-knockback'] : [],
    teams: [],
    modeScoreLimit: 20,
    teamStocks: 4,
    rouletteSeconds: 12,
    enabledWeapons: 'all',
    // Save both arena sizes. Selecting a larger party must not erase its theme.
    enabledLevels: id === 'pirate' || id === 'neon' ? themedLevels[id].map((level) => level.id) : 'all',
  };
}

/** A manual loadout, arena selection, or rule variation is visibly Custom instead of mislabelled. */
export function selectedPartyPreset<T extends Partial<PartyPresetConfig>>(config: T): PartyPresetId | 'custom' {
  if (!sameSelection(config.enabledWeapons, 'all')) return 'custom';
  const mode = config.mode ?? 'classic';
  const mutators = config.mutators ?? [];
  if (mode === 'weapon-roulette' && sameSelection(mutators, ['low-gravity', 'giant-knockback'])
    && (config.rouletteSeconds ?? 12) === 12 && sameSelection(config.enabledLevels, 'all')) return 'chaos';
  if (mutators.length) return 'custom';
  if (mode === 'classic') {
    for (const theme of ['pirate', 'neon'] as const) {
      if (sameSelection(config.enabledLevels, themedLevels[theme].map((level) => level.id))) return theme;
    }
  }
  if (!sameSelection(config.enabledLevels, 'all')) return 'custom';
  return mode === 'classic' || mode === 'arms-race' || mode === 'office-brawl' || mode === 'teams' ? mode : 'custom';
}

/** Resolve a saved playlist for this roster without letting the generic empty-pool fallback change its theme. */
export function resolvePresetLevels(config: Pick<PartyPresetConfig, 'enabledLevels'>, fighters: number): string[] | 'all' {
  const count = Number.isFinite(fighters) ? Math.max(1, Math.min(MAX_PLAYERS, Math.ceil(fighters))) : 4;
  for (const theme of ['pirate', 'neon'] as const) {
    const levels = themedLevels[theme];
    if (!sameSelection(config.enabledLevels, levels.map((level) => level.id))) continue;
    const compatible = levels.filter((level) => levelCapacity(level) >= count);
    const compact = compatible.filter((level) => levelCapacity(level) <= 4);
    return (count <= 4 && compact.length ? compact : compatible).map((level) => level.id);
  }
  return config.enabledLevels === 'all' ? 'all' : [...config.enabledLevels];
}

/** Shared by the home screen and a connected party; native buttons also work with pad navigation. */
export function renderPartyPresets(
  selected: PartyPresetId | 'custom',
  onSelect: (preset: PartyPresetId) => void,
  options: { disabled?: boolean } = {},
): HTMLElement {
  const root = document.createElement('section');
  root.className = 'party-presets';
  root.setAttribute('aria-label', 'Game presets');
  const heading = document.createElement('div');
  heading.className = 'party-presets-heading';
  const title = document.createElement('h2');
  title.textContent = 'Choose your next brawl';
  const custom = document.createElement('span');
  custom.className = 'party-presets-custom';
  custom.textContent = 'Custom rules';
  custom.hidden = selected !== 'custom';
  heading.append(title, custom);
  const rail = document.createElement('div');
  rail.className = 'party-presets-rail';
  let selectedCard: HTMLButtonElement | undefined;
  for (const preset of PARTY_PRESETS) {
    const card = document.createElement('button');
    card.type = 'button';
    card.id = `party-preset-${preset.id}`;
    card.className = 'party-preset-card';
    card.dataset.preset = preset.id;
    card.style.setProperty('--preset-accent', preset.accent);
    card.setAttribute('aria-pressed', String(preset.id === selected));
    if (preset.id === selected) selectedCard = card;
    card.disabled = options.disabled ?? false;
    const preview = document.createElement('span');
    preview.className = 'party-preset-preview';
    preview.innerHTML = presetIllustrationSvg(preset.id);
    const name = document.createElement('strong');
    name.className = 'party-preset-title';
    name.textContent = preset.title;
    const description = document.createElement('span');
    description.className = 'party-preset-description';
    description.textContent = preset.description;
    card.append(preview, name, description);
    card.addEventListener('click', () => {
      for (const button of rail.querySelectorAll<HTMLButtonElement>('.party-preset-card')) {
        button.setAttribute('aria-pressed', String(button === card));
      }
      custom.hidden = true;
      onSelect(preset.id);
    });
    card.addEventListener('focus', () => card.scrollIntoView({ block: 'nearest', inline: 'nearest' }));
    rail.append(card);
  }
  root.append(heading, rail);
  // Selecting a preset rebuilds the home screen. Keep its card in view after the new rail mounts.
  requestAnimationFrame(() => {
    if (!root.isConnected || !selectedCard) return;
    const bounds = rail.getBoundingClientRect(), current = selectedCard.getBoundingClientRect();
    if (current.left < bounds.left) rail.scrollLeft += current.left - bounds.left;
    else if (current.right > bounds.right) rail.scrollLeft += current.right - bounds.right;
  });
  return root;
}
